import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/shared/icons';
import { Tooltip } from './Tooltip';

export interface WarningBarAction {
  label: string;
  /** Navegação interna — renderiza <Link> */
  to?: string;
  /** Ação imperativa — renderiza <button> */
  onClick?: () => void;
  'data-testid'?: string;
}

export interface WarningBarProps {
  /** Conteúdo da mensagem (aceita <strong> etc.) */
  message: React.ReactNode;
  /** Trunca a mensagem em 1 linha (senão quebra em múltiplas) */
  truncate?: boolean;
  /** CTA opcional após a mensagem */
  action?: WarningBarAction;
  /** Quando presente, exibe o X de dispensar à direita */
  onDismiss?: () => void;
  dismissTestId?: string;
  'data-testid'?: string;
}

const ACTION_CLASS =
  // self-stretch: área de toque ocupa a altura inteira da barra (mobile)
  'text-[12.5px] font-semibold underline transition-colors flex-shrink-0 self-stretch flex items-center';

const actionHoverProps = {
  style: { color: 'var(--theme-primary)' },
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = 'var(--theme-button-primary-hover)';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = 'var(--theme-primary)';
  },
};

/**
 * Barra de aviso de página, renderizada NO FLUXO (nunca overlay flutuante) —
 * primitivo dono da anatomia de "warning bar" (consumido pelo
 * DisconnectedChannelWidget e pelo FunnelNoUsersBanner do CRM).
 * Warning theme-aware via CSS variables; CTA como Link (`to`) ou button
 * (`onClick`); dismiss opcional via `onDismiss`.
 */
export const WarningBar: React.FC<WarningBarProps> = ({
  message,
  truncate = false,
  action,
  onDismiss,
  dismissTestId,
  'data-testid': dataTestId,
}) => (
  <div
    className="flex-shrink-0 flex items-center gap-2.5 px-4 py-1.5 w-full"
    style={{
      minHeight: '36px',
      backgroundColor: 'var(--theme-warning-background)',
      borderBottom: '1px solid var(--theme-warning-color)',
    }}
    data-testid={dataTestId}
  >
    <Icon
      name="alert-triangle"
      className="w-3.5 h-3.5 flex-shrink-0"
      style={{ color: 'var(--theme-warning-color)' }}
    />

    <span
      className={`text-[12.5px] font-medium${truncate ? ' truncate' : ''}`}
      style={{ color: 'var(--theme-card-text)' }}
    >
      {message}
    </span>

    {action &&
      (action.to ? (
        <Link
          to={action.to}
          className={ACTION_CLASS}
          {...actionHoverProps}
          data-testid={action['data-testid']}
        >
          {action.label}
        </Link>
      ) : (
        <button
          type="button"
          onClick={action.onClick}
          className={ACTION_CLASS}
          {...actionHoverProps}
          data-testid={action['data-testid']}
        >
          {action.label}
        </button>
      ))}

    <span className="flex-1" />

    {onDismiss && (
      <Tooltip content="Dispensar">
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 rounded-md transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.color = 'var(--theme-card-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
          aria-label="Dispensar aviso"
          data-testid={dismissTestId}
        >
          <Icon name="x" className="w-3.5 h-3.5" />
        </button>
      </Tooltip>
    )}
  </div>
);
